<template>
  <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
    <RouterLink
      v-slot="{ navigate }"
      :to="{
        name: 'MobilePlantRecord',
        params: {
          plantId: plant.id
        },
        query: {
          day: `${dateAsYMD($props.selectedDate)}`
        }
      }"
      custom
    >
      <div
        class="flex items-center justify-between w-full p-4 hover:bg-gray-50 transition-colors duration-200"
        :class="[plantClass(plant)]"
        data-test="plant-row"
        @click="plant.dailyRecords($props.selectedDate).length === 0 ? navigate() : open = !open"
      >
        <div class="flex items-center space-x-3 flex-1">
          <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-blue-600">
<path fill-rule="evenodd" clip-rule="evenodd" d="M4.87171 1.58811C4.18324 1.9184 4.34293 2.75714 4.79289 3.20711L9.08579 7.5L7.5 9.08579L3.20711 4.79289C2.75711 4.3429 1.90944 4.19971 1.58419 4.87995C0.985604 6.1319 0.769329 7.54124 0.975579 8.9287C1.23113 10.6478 2.11708 12.2104 3.46105 13.3124C4.80502 14.4144 6.51089 14.977 8.24676 14.8908C8.89048 14.8588 9.52124 14.7385 10.1228 14.537L17.8787 22.2929C19.0503 23.4645 20.9497 23.4645 22.1213 22.2929L22.2929 22.1213C23.4645 20.9497 23.4645 19.0503 22.2929 17.8787L14.537 10.1228C14.7395 9.51835 14.8601 8.8844 14.8913 8.2375C14.9753 6.50151 14.4104 4.79638 13.3066 3.45387C12.2028 2.11135 10.6391 1.22746 8.91964 0.974169C7.53192 0.769745 6.12286 0.987876 4.87171 1.58811ZM12.0945 10.5087C12.5356 9.7995 12.793 8.98448 12.834 8.13802C12.8932 6.91285 12.4946 5.70946 11.7156 4.76198C10.9366 3.8145 9.83297 3.19069 8.61946 3.01193C8.21216 2.95193 7.80225 2.94351 7.39883 2.98462L10.5 6.08579C11.2811 6.86684 11.281 8.13317 10.5 8.91421L8.91421 10.5C8.13317 11.281 6.86684 11.281 6.08579 10.5L2.98467 7.39888C2.94336 7.8044 2.95209 8.21647 3.01295 8.62584C3.1933 9.8391 3.81856 10.9419 4.76707 11.7196C5.71557 12.4974 6.91949 12.8944 8.14458 12.8336C8.98967 12.7916 9.80314 12.5338 10.511 12.093L11.9946 13.5766L11.9924 13.5782L19.2929 20.8787C19.6834 21.2692 20.3166 21.2692 20.7071 20.8787L20.8787 20.7071C21.2692 20.3166 21.2692 19.6834 20.8787 19.2929L12.0945 10.5087Z" fill="currentColor"/>
</svg>
          </div>

          <div class="flex-1">
            <p class="font-medium text-gray-900">
              {{ plant.fleet }}
            </p>

            <p class="text-sm text-gray-500">
              {{ plant.description }}
            </p>

            <p class="text-xs text-gray-500 mt-1">
              {{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.hireRate') }} <span class="font-medium text-gray-900">{{ plant.wet ? $t('Mobile.MobilePlantDashboard.PlantRecordRow.hour') : $t('Mobile.MobilePlantDashboard.PlantRecordRow.day') }}</span>
            </p>
          </div>
        </div>

        <div class="flex items-center space-x-3">
          <div class="text-right">
            <p class="font-semibold text-gray-900">
              {{ $td(plant.records.length ? plant.dailyHours($props.selectedDate) : 0) }}
            </p>

            <p class="text-xs text-gray-500">
              {{ plant.smu_tracking ? $t('Mobile.MobilePlantDashboard.PlantRecordRow.smu') : $t('Mobile.MobilePlantDashboard.PlantRecordRow.hours') }}
            </p>
          </div>

          <RouterLink
            v-if="$props.projectUnarchived"
            class="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors duration-200"
            :to="{
              name: 'MobilePlantRecord',
              params: {
                plantId: plant.id
              },
              query: {
                day: `${dateAsYMD($props.selectedDate)}`
              }
            }"
            data-test="add-record"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 text-white">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>
          </RouterLink>

          <button
            v-if="plant.dailyRecords($props.selectedDate).length > 0"
            type="button"
            class="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-gray-600 transition-transform duration-200"
              :class="{ 'rotate-180': open }">
  <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
</svg>
          </button>
        </div>
      </div>
    </RouterLink>

    <!-- Expanded Records -->
    <div
      v-if="open && plant.dailyRecords($props.selectedDate).length > 0"
      class="border-t border-gray-100"
      :class="[plantClass(plant)]"
      data-test="plant-record-table"
    >
      <div class="p-4">
        <h4 class="text-sm font-medium text-gray-700 mb-3">
          {{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.dailyRecords') }}
        </h4>

        <div class="space-y-2">
          <RouterLink
            v-for="record in plant.dailyRecords($props.selectedDate)"
            :key="record.id"
            class="block bg-white rounded-lg p-3 hover:bg-gray-50 transition-colors duration-200 border border-gray-200 relative"
            :to="{
              name: 'MobilePlantRecord',
              params: {
                plantId: plant.id,
                recordId: record.id
              }
            }"
          >
            <!-- Standdown and Breakdown Indicators -->
            <div class="absolute top-2 right-2 flex space-x-1">
              <div
                v-if="record.standdown > 0"
                class="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center"
                :title="$t('Mobile.MobilePlantDashboard.PlantRecordRow.standdownTimeTooltip')"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    stroke-width="2"
 class="w-3 h-3 text-orange-600">
    <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
    />

    <path
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 6v6l4 2"
    />
</svg>
              </div>

              <div
                v-if="record.breakdown > 0"
                class="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center"
                :title="$t('Mobile.MobilePlantDashboard.PlantRecordRow.breakdownTimeTooltip')"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    stroke-width="2"
 class="w-3 h-3 text-red-600">
    <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
    />

    <path
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 6v6l4 2"
    />
</svg>
              </div>
            </div>

            <div class="flex items-center space-x-2">
              <span class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                {{ record.wbsCode.code }}
              </span>

              <span class="text-sm font-medium text-gray-900">
                {{
                  $td(record.hours)
                }}{{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.unit.hour') }}
              </span>
            </div>

            <!-- SMU Display for SMU Tracking Plants -->
            <div
              v-if="plant.smu_tracking && record.smu_start && record.smu_end"
              class="flex justify-between items-center mt-2 text-xs text-gray-600"
            >
              <p>
                {{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.smuStart') }} <span class="font-medium">{{ record.smu_start }}</span>
              </p>

              <p>
                {{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.smuEnd') }} <span class="font-medium">{{ record.smu_end }}</span>
              </p>
            </div>

            <!-- Hours Breakdown -->
            <div
              v-if="record.standdown > 0 || record.breakdown > 0"
              class="flex justify-between items-center mt-2 text-xs text-gray-600"
            >
              <p>
                {{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.totalTime') }}

                <span class="font-medium">{{ $td(record.hoursWithoutStanddownBreakdown) }}{{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.unit.hour') }}</span>
              </p>

              <p
                v-if="record.standdown > 0"
              >
                {{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.standdown') }}

                <span class="font-medium text-orange-600">
                  {{ $td(record.standdown) }}{{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.unit.hour') }}
                </span>
              </p>

              <p
                v-if="record.breakdown > 0"
              >
                {{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.breakdown') }}

                <span class="font-medium text-orange-600">
                  {{ $td(record.breakdown) }}{{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.unit.hour') }}
                </span>
              </p>
            </div>

            <span class="text-xs">
              {{ record.wbsCode.description }}
            </span>

            <div class="space-y-0.5">
              <div class="flex justify-between text-sm">
                <span class="text-gray-500">{{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.area') }}</span>

                <span class="text-gray-900 font-medium">{{ record.area }}</span>
              </div>

              <div class="flex justify-between text-sm">
                <span class="text-gray-500">{{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.element') }}</span>

                <span class="text-gray-900 font-medium">{{ record.element }}</span>
              </div>

              <div class="flex justify-between text-sm">
                <span class="text-gray-500">{{ $t('Mobile.MobilePlantDashboard.PlantRecordRow.activity') }}</span>

                <span class="text-gray-900 font-medium">{{ record.activity }}</span>
              </div>
            </div>
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { dateAsYMD } from '@/helpers';
import Plant from '@/models/Plant';
import Project from '@/models/Project';
import { ref } from 'vue';

const props = defineProps<{
  plant: Plant;
  selectedDate: Date;
  project: Project;
  projectUnarchived: boolean;
}>();

const plantClass = (plant: Plant) => {
  if(plant.dailyHoursWithoutStanddownBreakdown(props.selectedDate) >= props.project?.plant_standard_shift) {
    return 'border-green-200 bg-green-100';
  } else if(plant.dailyHoursWithoutStanddownBreakdown(props.selectedDate) > 0) {
    return 'border-orange-200 bg-orange-100';
  } else {
    return 'border-red-200 bg-red-100';
  }
};

const open = ref(false);
</script>

<style scoped></style>
